// Display helpers for the driver surface. These are pure presentation —
// they never recompute money. `amount` arrives as a fixed-2 decimal
// string straight from the numeric(12,2) column; we only format it.

// Format a numeric(12,2) money string (e.g. "1450.5") as US currency
// ("$1,450.50"). Locale is pinned to en-US so the rendering is the same
// on every device and deterministic under test. Null/blank/unparseable
// values return null — callers decide what to show instead (the list
// shows an "Add amount" affordance rather than a placeholder dash).
export function formatAmount(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

// Format an ISO timestamp as a short date ("Aug 15"). Pinned to en-US
// for the same reason as formatAmount. Returns "—" when absent/invalid.
export function formatLoadDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(d);
}

// Move types are stored as EMPTY_IN / FULL_OUT / … — shout-case enum
// values that belong in the database, not in front of a driver. Render
// them as "Empty in" / "Full out".
export function formatMoveType(value: string | null | undefined): string | null {
  if (!value) return null;
  const words = value.toLowerCase().split("_").filter(Boolean);
  if (words.length === 0) return null;
  const [first, ...rest] = words as [string, ...string[]];
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(" ");
}

// Gross weight is numeric(10,2); drivers read it in whole pounds.
export function formatWeightLb(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n)} lb`;
}
